<template>
  <XSFlipCard :flipped="isFlipped">
    <template #front>
      <div>
        <FileInput
          v-bind="$attrs"
          :fileChangeHandler="(file) => fileChangeHandler(file)"
          :loadingStatus="loadingStatus()"
          :image_url="assetId()"
          :image_url_brief="briefAssetId()"
          :previewUrl="previewUrl()"
          :previewUrlBrief="previewUrlBrief()"
          :diffIdentifier="diffIdentifier"
          :isAssetApproved="assetApproved"
          :canFinalizeAsset="$attrs.canFinalizeAsset"
          @addBriefToAsset="
            (previewUrl, briefUrl, isAssetApproved) =>
              copyFile(previewUrl, briefUrl, 0, isAssetApproved)
          "
          :canShowSecondaryAssetCheckbox="true"
          secondaryCheckboxLabel="Teacher View"
          @secondaryCheckBoxClicked="onUpdateSecondaryCheckbox"
          :isSecondayCheckboxChecked="isSecondayCheckboxChecked"
          @flipToggled="flipToggled"
          :isFlipped="isFlipped"
          :canShowFlipToggle="true"
          :assetApprovedCheckboxColor="assetApprovedCheckboxColor"
        />
        <div class="flip-toggle" v-if="currentAssetId() && !isVideoAsset">
          <v-btn outlined @click="isFlipped = !isFlipped" color="#DC143C">
            Upload Answer
          </v-btn>
        </div>

        <RichTextEditor
          :canShowDiffIcon="$attrs.canShowDiffIcon"
          :isDiffChanged="true"
          :fieldIdentifier="`${fileIdentifier}.brief`"
          placeholder="brief"
          class="text-field-text"
          style="margin: 25px 0px 0px 0px;"
          :showValidationError="!$attrs.isAssetUploadRequired"
          v-model="sectionBrief"
          :counter="3000"
          :readonly="false"
          :includeOnlyToolBarActions="BASIC_TOOLS"
        />
      </div>
    </template>

    <template #back>
      <div>
        <FileInput
          v-bind="$attrs"
          :fileChangeHandler="(file) => fileChangeHandler(file)"
          :loadingStatus="loadingStatus()"
          :image_url="assetId()"
          :image_url_brief="briefAssetId()"
          :previewUrl="previewUrl()"
          :previewUrlBrief="previewUrlBrief()"
          :diffIdentifier="diffIdentifier"
          :isAssetApproved="assetApproved"
          :canFinalizeAsset="$attrs.canFinalizeAsset"
          @addBriefToAsset="
            (previewUrl, briefUrl, isAssetApproved) =>
              copyFile(previewUrl, briefUrl, 0, isAssetApproved)
          "
          :canShowSecondaryAssetCheckbox="true"
          secondaryCheckboxLabel="Teacher View"
          @secondaryCheckBoxClicked="onUpdateSecondaryCheckbox"
          :isSecondayCheckboxChecked="isSecondayCheckboxChecked"
          @flipToggled="flipToggled"
          :isFlipped="isFlipped"
          :canShowFlipToggle="true"
          :assetApprovedCheckboxColor="assetApprovedCheckboxColor"
        />
        <div class="flip-toggle">
          <v-btn outlined @click="isFlipped = !isFlipped" color="#DC143C">
            Upload Question
          </v-btn>
        </div>

        <RichTextEditor
          :canShowDiffIcon="$attrs.canShowDiffIcon"
          :isDiffChanged="true"
          :fieldIdentifier="`${fileIdentifier}.brief`"
          placeholder="brief"
          class="text-field-text"
          style="margin: 25px 0px 0px 0px;"
          :showValidationError="!$attrs.isAssetUploadRequired"
          v-model="sectionBrief"
          :counter="3000"
          :readonly="false"
          :includeOnlyToolBarActions="BASIC_TOOLS"
        />
      </div>
    </template>
  </XSFlipCard>
</template>

<script>
import RichTextEditor, { BASIC_TOOLS } from '@/components/RTE/RichTextEditor.vue';
import FileInput from '@/components/inputs/FileInput';
import { mapActions } from 'vuex';
import FILE_UPLOADING_STATUS from '@/constants/FileUploadingStatus';
import { get } from 'lodash';
import { setNestedObject } from '@/utils/common.js';
import XSFlipCard from '@/components/lessonPlanTemplate/commonLpComponents/XSFlipCard.vue';

export default {
  name: 'FileInputWrapper',
  components: {
    FileInput,
    RichTextEditor,
    XSFlipCard,
  },
  data() {
    return {
      FILE_UPLOADING_STATUS,
      BASIC_TOOLS,
      isFlipped: false,
    };
  },
  computed: {
    data() {
      return this.$attrs.data;
    },
    assetApproved() {
      return get(this.data, `${this.fileIdentifier}.assetApproved`, false);
    },
    assetApprovedCheckboxColor() {
      const fieldIdentifierObject = get(this.data, `${this.fileIdentifier}`, {});
      const reverseFieldIdentifierObject = get(this.data, `${this.reverseFileIdentifier}`, {});

      if (!this.reverseCurrentAssetId()) {
        return '#37B074';
      }

      return fieldIdentifierObject.assetApproved && reverseFieldIdentifierObject.assetApproved
        ? '#37B074'
        : '#cc9900';
    },
    subPath() {
      return this.$attrs.subPath;
    },
    dataPath() {
      let dataPath = this.$attrs.dataPath;

      if (this.isFlipped) {
        dataPath = `${dataPath}.alt`;
      }
      return dataPath;
    },
    reverseDataPath() {
      let dataPath = this.$attrs.dataPath;

      if (!this.isFlipped) {
        dataPath = `${dataPath}.alt`;
      }
      return dataPath;
    },
    imageKey() {
      return this.$attrs.isAssetUploadRequired ? 'assetId' : 'brief.assetId';
    },
    diffIdentifier() {
      return `${this.fileIdentifier}.${this.imageKey}`;
    },
    fileIdentifier() {
      return `${this.subPath}.${this.dataPath}`;
    },
    reverseFileIdentifier() {
      return `${this.subPath}.${this.reverseDataPath}`;
    },
    assetSubPath() {
      return this.$attrs.assetSubPath;
    },
    assetUrlIdentifier() {
      return `${this.assetSubPath || this.subPath}.${this.dataPath}`;
    },
    sectionBrief: {
      get() {
        return get(this.data, `${this.fileIdentifier}.brief.text`);
      },
      set(val) {
        setNestedObject(this.data, `${this.fileIdentifier}.brief.text`, val);
      },
    },
    isSecondayCheckboxChecked: {
      get() {
        let isTeacherViewSelected = get(
          this.data,
          `${this.fileIdentifier}.showInTeacherView`,
          undefined
        );
        if (isTeacherViewSelected === undefined) {
          setNestedObject(this.data, `${this.fileIdentifier}.showInTeacherView`, true);
          isTeacherViewSelected = true;
        }
        return isTeacherViewSelected;
      },
      set(val) {
        setNestedObject(this.data, `${this.fileIdentifier}.showInTeacherView`, val);
      },
    },
    isVideoAsset() {
      const url = this.isAssetUploadRequired ? this.previewUrlBrief() : this.previewUrl();
      if (!url) return false;
      return /\.mp4($|\?)/i.test(url);
    },
  },
  methods: {
    ...mapActions([
      'importSingleAssetActionByIdentifier',
      'deleteSingleResourceAssetActionByIdentifier',
      'copySingleResourceAssetActionByIdentifier',
    ]),
    loadingStatus() {
      const currentLoadingStatus = get(this.$attrs.signedUrls, this.assetUrlIdentifier, {})
        .loadingStatus;
      return currentLoadingStatus == FILE_UPLOADING_STATUS.IN_PROGRESS;
    },
    currentAssetId() {
      if (this.$attrs.isAssetUploadRequired) {
        return get(this.$attrs.data, `${this.fileIdentifier}.assetId`, '');
      }
      return get(this.$attrs.data, `${this.fileIdentifier}.brief.assetId`, '');
    },
    reverseCurrentAssetId() {
      if (this.$attrs.isAssetUploadRequired) {
        return get(this.$attrs.data, `${this.reverseFileIdentifier}.assetId`, '');
      }
      return get(this.$attrs.data, `${this.reverseFileIdentifier}.brief.assetId`, '');
    },
    assetId() {
      if (this.$attrs.isAssetUploadRequired) {
        return get(this.$attrs.data, this.fileIdentifier, {}).assetId;
      }
      return '';
    },
    previewUrl() {
      if (this.$attrs.isAssetUploadRequired) {
        return get(this.$attrs.signedUrls, this.assetUrlIdentifier, {}).assetSignedUrl;
      }
      return '';
    },
    briefAssetId() {
      return get(this.$attrs.data, this.fileIdentifier, {})?.brief?.assetId ?? '';
    },
    previewUrlBrief() {
      return get(this.$attrs.signedUrls, this.assetUrlIdentifier, {})?.brief?.assetSignedUrl ?? '';
    },
    fileChangeHandler(file) {
      if (file) {
        this.importSingleAssetActionByIdentifier({
          payload: file,
          identifier: this.fileIdentifier,
          assetUrlIdentifier: this.assetUrlIdentifier,
        });
      } else {
        this.deleteSingleResourceAssetActionByIdentifier({
          assetId: this.currentAssetId(),
          identifier: this.fileIdentifier,
          subPath: this.subPath,
          assetUrlIdentifier: this.assetUrlIdentifier,
        });
      }
    },
    copyFile(signedUrl, briefAssetId, isAssetApproved) {
      this.copySingleResourceAssetActionByIdentifier({
        signedUrl,
        briefAssetId,
        isAssetApproved,
        identifier: this.fileIdentifier,
        assetUrlIdentifier: this.assetUrlIdentifier,
      });
    },
    onUpdateSecondaryCheckbox(status) {
      this.isSecondayCheckboxChecked = status;
    },
    flipToggled(status) {
      console.log('Flipped status:', status);
      this.isFlipped = status;
    },
  },
};
</script>

<style scoped>
.flip-toggle {
  display: flex;
  justify-content: center;
  margin: 12px 0;
}
</style>
